import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, Eye, EyeOff, User, Phone, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

export default function RegisterPage() {
  const { t } = useTranslation();
  const { register, verifyEmail } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultRole = searchParams.get('role') || 'customer';
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    role: defaultRole,
    referral_code: searchParams.get('ref') || '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verificationStep, setVerificationStep] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        role: formData.role,
        referral_code: formData.referral_code,
      });
      toast.success('Registration successful! Please check your email.');
      setVerificationStep(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await verifyEmail(formData.email, verificationCode);
      toast.success('Email verified! You can now login.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { value: 'customer', labelKey: 'auth.role.customer' },
    { value: 'driver', labelKey: 'auth.role.driver', redirect: '/apply/driver' },
    { value: 'restaurant_admin', labelKey: 'auth.role.restaurant' },
    { value: 'sales_person', labelKey: 'auth.role.salesperson', redirect: '/apply/salesperson' },
  ];

  // Handle role change - redirect driver/salesperson to dedicated forms
  const handleRoleChange = (value) => {
    const selectedRole = roles.find(r => r.value === value);
    if (selectedRole?.redirect) {
      navigate(selectedRole.redirect);
      return;
    }
    setFormData({ ...formData, role: value });
  };

  return (
    <>
      <Helmet>
        <title>Sign Up - Hambre | Create Your Account</title>
        <meta name="description" content="Create your Hambre account and start ordering delicious food from local restaurants." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen gradient-hero flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-xl shadow-orange-500/10 p-8">
            {/* Logo */}
            <div className="text-center mb-8">
              <Link to="/" className="inline-block">
                <svg viewBox="0 0 100 100" className="w-16 h-16 mx-auto">
                  <rect width="100" height="100" rx="20" fill="#F97316"/>
                  <g fill="white">
                    <rect x="18" y="25" width="4" height="50" rx="2"/>
                    <rect x="25" y="25" width="3" height="20" rx="1.5"/>
                    <rect x="18" y="25" width="3" height="20" rx="1.5"/>
                    <rect x="11" y="25" width="3" height="20" rx="1.5"/>
                    <ellipse cx="78" cy="35" rx="12" ry="15"/>
                    <rect x="76" y="45" width="4" height="30" rx="2"/>
                  <rect x="30" y="50" width="44" height="8" rx="4"/>
                </g>
              </svg>
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 mt-4">
              {verificationStep ? t('auth.verifyEmail') : t('auth.signupTitle', 'Create Account')}
            </h1>
            <p className="text-slate-500 mt-2">
              {verificationStep ? t('auth.enterCode', 'Enter the code sent to your email') : t('auth.signupSubtitle', 'Join Hambre today')}
            </p>
          </div>

          {verificationStep ? (
            <form onSubmit={handleVerification} className="space-y-6">
              <div>
                <Label htmlFor="verification-code">{t('auth.verificationCode')}</Label>
                <Input
                  id="verification-code"
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder={t('auth.enterCodePlaceholder', 'Enter 6-digit code')}
                  className="mt-2 text-center text-2xl tracking-widest"
                  maxLength={6}
                  required
                  data-testid="verification-code-input"
                />
              </div>
              <Button 
                type="submit" 
                className="btn-primary w-full" 
                disabled={loading}
                data-testid="verify-button"
              >
                {loading ? t('auth.verifying', 'Verifying...') : t('auth.verifyEmailBtn', 'Verify Email')}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <Label htmlFor="role">{t('auth.chooseRole', 'Choose your role from the drop down')}</Label>
                <Select value={formData.role} onValueChange={handleRoleChange}>
                  <SelectTrigger className="mt-2" data-testid="role-select">
                    <SelectValue placeholder={t('auth.selectRole', 'Select your role')} />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {t(role.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="name">{t('auth.name')}</Label>
                <div className="relative mt-2">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="pl-10"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">{t('auth.email')}</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className="pl-10"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone">{t('auth.phone')}</Label>
                <div className="relative mt-2">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+44 123 456 7890"
                    className="pl-10"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password">{t('auth.password')}</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="pl-10"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              {formData.referral_code && (
                <div>
                  <Label htmlFor="referral_code">{t('auth.referralCode')}</Label>
                  <Input
                    id="referral_code"
                    name="referral_code"
                    type="text"
                    value={formData.referral_code}
                    onChange={handleChange}
                    className="mt-2"
                    data-testid="referral-code-input"
                  />
                </div>
              )}

              <Button 
                type="submit" 
                className="btn-primary w-full mt-6" 
                disabled={loading}
                data-testid="register-button"
              >
                {loading ? t('auth.creatingAccount') : t('auth.register')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              {/* Google Sign Up */}
              {formData.role === 'customer' && (
                <>
                  <div className="relative mt-4">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-slate-200" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-white text-slate-500">{t('auth.orSignUpWith', 'or sign up with')}</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full mt-4 border-slate-200 hover:bg-slate-50"
                    onClick={() => {
                      const redirectUrl = window.location.origin + '/auth/callback';
                      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                    }}
                    data-testid="google-signup-button"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    {t('auth.continueWithGoogle', 'Continue with Google')}
                  </Button>
                </>
              )}
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-slate-500">
              {t('auth.hasAccount')}{' '}
              <Link to="/login" className="text-orange-500 font-medium hover:text-orange-600">
                {t('auth.login')}
              </Link>
            </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
